// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * Every path unfence will write to passes through `checkPath` first — the
 * directory-handle route AND the zip route, because a zip is the mobile path
 * and `fflate`/`client-zip` sanitise nothing (zip slip).
 *
 * WHAT THE PLATFORM GIVES US IS ALMOST NOTHING. The WHATWG File System
 * Standard's whole definition of a valid file name is: not empty, not "." or
 * "..", and containing no "/" — and violating it throws a TypeError rather than
 * a SecurityError, so the obvious `catch (e) { if (e.name === 'NotAllowedError') }`
 * misclassifies it. Measured through OPFS, the platform happily accepts `a:b`,
 * `a*b`, `a?b`, `a|b`, `a<b`, `con`, and `app.ts ` with a trailing space, and it
 * accepts a whole Windows path `C:\Windows\System32\drivers\etc\hosts` on POSIX
 * because a backslash is a legal filename character there.
 *
 * Chromium adds a much better check of its own (`IsSafePathComponent`), but it
 * is one vendor's non-normative code carrying its own `TODO(crbug…)`, so the
 * rules below re-implement its substance rather than depend on it.
 *
 * AND THE ONE THING PEOPLE ASSUME IS PROTECTED IS NOT. Chromium has exactly one
 * `.git` rule — `BlockPath::CreateSuffix(".git/hooks", kBlockWrite)` — and it is
 * inside `#if BUILDFLAG(IS_WIN)`, and it is further neutered because the block
 * only applies to `UserAction::kSave` while `getFileHandle()` passes
 * `UserAction::kNone`. A web page with a directory handle can write
 * `.git/hooks/pre-commit` on every operating system. Rules 13 and 15 below are
 * the only thing standing there, which is why the threat model says so out loud.
 */

import type { PathVerdict, RefusalCode } from '../types';

const MAX_PATH = 255;
const MAX_SEGMENT = 100;
const MAX_SEGMENTS = 32;

/**
 * Windows device names, refused on EVERY platform.
 *
 * Not because we expect to be running on Windows — because the folder may be on
 * a share, in a container, or synced to a machine that is, and a file called
 * `con.ts` is a footgun everywhere. Matched after trimming trailing dots and
 * spaces and after dropping the extension, so `con.ts` matches as well as `con`.
 */
const WINDOWS_DEVICES = new Set([
  'con', 'prn', 'aux', 'nul', 'clock$', 'conin$', 'conout$', 'desktop.ini', 'thumbs.db',
  ...Array.from({ length: 9 }, (_, i) => `com${i + 1}`),
  ...Array.from({ length: 9 }, (_, i) => `lpt${i + 1}`),
]);

/** Illegal in a Windows filename. Refused everywhere, for the reason above. */
const WINDOWS_ILLEGAL = /["*:<>?|]/;

/**
 * Directories that are somebody's identity, somebody's credentials, or a
 * package manager's cache. Matched at ANY depth: `sub/.git/hooks/pre-commit`
 * walks straight past a check that only looks at the first segment.
 */
const SENSITIVE_DIRS = new Set([
  '.git', '.hg', '.svn', '.bzr', '.ssh', '.gnupg', '.gpg', '.aws', '.azure', '.kube',
  '.docker', '.npm', '.cargo', '.gem', '.m2', '.gradle', '.terraform', '.direnv',
  'node_modules', '.venv', 'venv', '__pycache__',
]);

/** Files that ARE a credential, or that name where one lives. */
const CREDENTIAL_BASENAMES = new Set([
  '.envrc', '.netrc', '_netrc', '.npmrc', '.pypirc', '.git-credentials', '.htpasswd',
  '.pgpass', 'kubeconfig', 'credentials', '.dockercfg', 'terraform.tfstate',
  'terraform.tfstate.backup', 'service-account.json', 'secrets.json', 'secrets.yml',
  'secrets.yaml', 'secrets.toml',
]);

const CREDENTIAL_STEMS = /^(id_rsa|id_dsa|id_ecdsa|id_ed25519|id_xmss)(\.pub)?$/;
const CREDENTIAL_EXTS = new Set(['pem', 'key', 'p12', 'pfx', 'jks', 'keystore', 'gpg', 'ppk', 'asc']);

/**
 * THE CATEGORY THE ORIGINAL SPEC OMITTED, AND WHERE ALL THE DAMAGE IS.
 *
 * Every entry here is a file that, once written, causes code to run without
 * anybody choosing to run it — on the next commit, the next `npm install`, the
 * next time the folder is opened in an editor, or the next `git push`.
 *
 * `.github/workflows/**` is the worst of them and it is worth being explicit
 * about why: a workflow file runs on GitHub's runner with the repository's
 * secrets in the environment and unrestricted network egress. It is the single
 * path by which a crafted reply could defeat "zero backend" entirely — the
 * exfiltration would happen from CI, hours later, with nothing in this tab to
 * see. Measured: `git apply` refuses `.git/hooks/pre-commit` outright and
 * ACCEPTS `.github/workflows/ci.yml`, so this refusal is ours alone.
 */
const EXECUTES_CODE_SEGMENTS = new Set([
  '.github', '.gitlab', '.circleci', '.vscode', '.idea', '.devcontainer', '.husky',
  '.claude', '.cursor', '.mvn', '.ebextensions',
]);
const EXECUTES_CODE_BASENAMES = new Set([
  'package.json', 'makefile', 'gnumakefile', 'justfile', 'rakefile', 'gemfile',
  'dockerfile', 'vagrantfile', 'gulpfile.js', 'gradle.properties', 'build.gradle',
  'setup.py', 'conftest.py', 'sitecustomize.py', 'usercustomize.py', 'claude.md',
  'binding.gyp', 'gradlew', 'mvnw',
]);
const EXECUTES_CODE_EXTS = new Set(['pth']);

/** Anything the operating system will happily execute or load. */
const EXECUTABLE_EXTS = new Set([
  'exe', 'dll', 'so', 'dylib', 'bat', 'cmd', 'com', 'scr', 'msi', 'vbs', 'ps1', 'psm1',
  'pif', 'cpl', 'jar', 'apk', 'dmg', 'pkg', 'deb', 'rpm', 'app', 'sh', 'zsh', 'bash',
  'fish', 'command', 'desktop', 'service', 'plist', 'lnk', 'scf', 'url', 'reg', 'inf',
  'hta', 'wsf', 'msc', 'gadget', 'workflow', 'action',
]);

/** unfence's own artefacts. A reply that names one overwrites the audit trail. */
const OWN_ARTEFACTS = [
  /^bundle\.md$/,
  /^changes\.patch$/,
  /^applied-.*\.patch$/,
  /^skipped\.md$/,
  /^backup-.*\.zip$/,
  /^updated\.zip$/,
  /^unfence-.*\.zip$/,
];

/**
 * The single normalisation every comparison uses.
 *
 * NFC first, then lower-case. Both halves are load-bearing: without casefolding,
 * `.GIT/hooks/pre-commit` and `.GitHub/workflows/ci.yml` walk straight through
 * every rule below and reach the real file on APFS and NTFS. `git apply` already
 * refuses `.GIT/...` (measured, rc=128 `invalid path`), so a writer that does
 * not casefold is weaker than the patch artefact it hands out.
 */
export function foldPath(path: string): string {
  return path.normalize('NFC').toLowerCase();
}

function refuse(code: RefusalCode, reason: string, overridable = false): PathVerdict {
  return { ok: false, code, reason, overridable, mixedScriptSegments: [] };
}

/** Script families that legitimately mix inside one identifier. */
const SCRIPT_TESTS: [string, RegExp][] = [
  ['Latin', /\p{Script=Latin}/u],
  ['Cyrillic', /\p{Script=Cyrillic}/u],
  ['Greek', /\p{Script=Greek}/u],
  ['Han', /\p{Script=Han}/u],
  ['Arabic', /\p{Script=Arabic}/u],
  ['Hebrew', /\p{Script=Hebrew}/u],
];

/**
 * Segments that mix Unicode scripts — `src/аpp.ts` with a Cyrillic а.
 *
 * SURFACED, NOT REFUSED, and that is the right call in both directions: a
 * blanket non-ASCII rejection breaks every legitimately non-English repository,
 * and the actual harm here is small. A homoglyph path does not overwrite the
 * file it resembles — it creates a NEW one beside it. So the mitigation is
 * display: flag the segment, render every path monospace, and show the code
 * point.
 */
function mixedScriptSegments(segments: string[]): string[] {
  const out: string[] = [];
  for (const seg of segments) {
    const scripts = SCRIPT_TESTS.filter(([, re]) => re.test(seg)).map(([name]) => name);
    if (scripts.length > 1) out.push(seg);
  }
  return out;
}

function extensionOf(basename: string): string {
  const dot = basename.lastIndexOf('.');
  return dot > 0 ? basename.slice(dot + 1) : '';
}

/**
 * Validate a path recovered from — or assigned against — a model's reply.
 *
 * Order matters. Whole-path gates run before the path is split on `/`, because
 * splitting first would let a newline hide inside a segment and be checked as
 * if it were an ordinary character.
 */
export function checkPath(raw: unknown): PathVerdict {
  /* ── whole-path gates ────────────────────────────────────────────────── */

  if (typeof raw !== 'string' || raw.length === 0) {
    return refuse('empty', 'The block has no file path.');
  }
  if (raw.length > MAX_PATH) {
    return refuse('too-long', `The path is ${raw.length} characters; the limit is ${MAX_PATH}.`);
  }

  // Control characters (\p{Cc}: NUL, newline, carriage return), invisible
  // formatting characters (\p{Cf}: zero-width space, and U+202E RIGHT-TO-LEFT
  // OVERRIDE, which is how `src/txt.exe` is made to display as `src/exe.txt`),
  // and Unicode noncharacters. Checked here, before the split, on purpose.
  if (/[\p{Cc}\p{Cf}]/u.test(raw)) {
    return refuse('control-char', 'The path contains an invisible or control character.');
  }
  if (/[\uFDD0-\uFDEF\uFFFE\uFFFF]/u.test(raw)) {
    return refuse('control-char', 'The path contains a Unicode noncharacter.');
  }

  // U+FFFD REPLACEMENT CHARACTER means the input was already mangled before it
  // reached us, and nobody ever means to create a file with one in its name.
  // It is specifically how a NUL arrives: CommonMark requires a parser to
  // replace U+0000 with U+FFFD, so `src/app<NUL>.ts` in a fence info string is
  // laundered into something that passes a control-character check. Refusing the
  // replacement character closes that, and the injection suite is what found it.
  if (raw.includes('\uFFFD')) {
    return refuse('control-char', 'The path contains a replacement character, so it arrived corrupted.');
  }

  // A backslash is never accepted and never translated. On POSIX it is a legal
  // filename character, so the platform's own validity test passes
  // `C:\Windows\System32\drivers\etc\hosts` as a single valid file name —
  // measured. Translating it to `/` would be worse still: it would turn a
  // rejected string into a working traversal.
  if (raw.includes('\\')) {
    return refuse('backslash', 'The path contains a backslash. Use forward slashes.');
  }

  if (/^[A-Za-z]:/.test(raw)) return refuse('absolute', 'The path starts with a drive letter.');
  if (raw.startsWith('/')) return refuse('absolute', 'The path is absolute.');
  if (raw.startsWith('~')) return refuse('absolute', 'The path is relative to a home directory.');
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw)) return refuse('absolute', 'The path looks like a URL.');

  // Nothing in unfence percent-decodes, so `%2f` cannot escape anything today.
  // It is refused on INTENT: the path travels into a zip, into a patch file and
  // into whatever the user runs next, and the first consumer that decodes turns
  // this into a real traversal.
  if (/%(2f|5c|252f|255c)/i.test(raw)) {
    return refuse('percent-encoded', 'The path contains a percent-encoded separator.');
  }

  const segments = raw.normalize('NFC').split('/');
  if (segments.length > MAX_SEGMENTS) {
    return refuse('too-many-segments', `The path has ${segments.length} segments; the limit is ${MAX_SEGMENTS}.`);
  }

  /* ── per-segment gates ───────────────────────────────────────────────── */

  for (const seg of segments) {
    if (seg.length === 0) {
      return refuse('empty-segment', 'The path has an empty segment (a leading, trailing or doubled slash).');
    }
    // EXACT equality, never `includes('..')`. A substring test rejects the
    // perfectly ordinary `..eslintrc.js` and catches nothing extra: `....//`
    // survives it and is caught here anyway, because its segments are `....`
    // and `` — the empty one above.
    if (seg === '.' || seg === '..') {
      return refuse('dot-segment', 'The path tries to leave the folder.');
    }
    if (seg !== seg.trim()) {
      return refuse('segment-whitespace', `"${seg}" starts or ends with whitespace.`);
    }
    if (seg.endsWith('.')) {
      return refuse('segment-trailing-dot', `"${seg}" ends with a dot.`);
    }
    if (seg.length > MAX_SEGMENT) {
      return refuse('segment-too-long', `"${seg.slice(0, 40)}…" is longer than ${MAX_SEGMENT} characters.`);
    }
    if (WINDOWS_ILLEGAL.test(seg)) {
      return refuse('windows-illegal', `"${seg}" contains a character that is illegal in a filename on Windows.`);
    }
    const device = seg.toLowerCase().replace(/[. ]+$/, '').split('.')[0];
    if (WINDOWS_DEVICES.has(device) || WINDOWS_DEVICES.has(seg.toLowerCase().replace(/[. ]+$/, ''))) {
      return refuse('windows-device', `"${seg}" is a reserved device name on Windows.`);
    }
  }

  /* ── policy gates: refused by default, overridable by typing ─────────── */

  const folded = segments.map((s) => s.toLowerCase());
  const basename = folded[folded.length - 1] ?? '';
  const ext = extensionOf(basename);
  const dirs = folded.slice(0, -1);

  for (const seg of folded) {
    if (SENSITIVE_DIRS.has(seg)) {
      return refuse('sensitive-directory', `"${seg}" holds credentials or repository internals.`, true);
    }
  }

  if (
    /^\.env($|\.)/.test(basename) ||
    CREDENTIAL_BASENAMES.has(basename) ||
    CREDENTIAL_STEMS.test(basename) ||
    CREDENTIAL_EXTS.has(ext)
  ) {
    return refuse('credential-file', `"${basename}" is a credential or a private key.`, true);
  }

  if (
    dirs.some((d) => EXECUTES_CODE_SEGMENTS.has(d)) ||
    EXECUTES_CODE_BASENAMES.has(basename) ||
    EXECUTES_CODE_EXTS.has(ext)
  ) {
    return refuse('executes-code', `"${basename}" causes code to run without anybody choosing to run it.`, true);
  }

  if (EXECUTABLE_EXTS.has(ext)) {
    return refuse('executable-file', `"${basename}" is an executable or a script the system will run.`, true);
  }

  if (OWN_ARTEFACTS.some((re) => re.test(basename))) {
    return refuse('own-artefact', `"${basename}" is one of unfence's own files.`);
  }

  return {
    ok: true,
    code: null,
    reason: null,
    overridable: false,
    mixedScriptSegments: mixedScriptSegments(segments),
  };
}

/**
 * Group paths that collide once the filesystem has had its say.
 *
 * On APFS and NTFS `src/App.ts` and `src/app.ts` are ONE file; on ext4 they are
 * two. A tool that cannot know which it is facing must not pick: merging them is
 * wrong on a case-sensitive disk, and not merging them destroys a file on a
 * case-insensitive one. So a colliding group is refused whole and listed, which
 * is the only answer that is never silently wrong.
 *
 * Returns a map from the folded key to every distinct raw path that folds to it.
 */
export function collisionGroups(paths: readonly string[]): Map<string, string[]> {
  const groups = new Map<string, string[]>();
  for (const p of paths) {
    const key = foldPath(p);
    const list = groups.get(key) ?? [];
    if (!list.includes(p)) list.push(p);
    groups.set(key, list);
  }
  for (const [key, list] of groups) if (list.length < 2) groups.delete(key);
  return groups;
}
